<template>
  <div class="effects-gallery">
    <div class="effects-gallery__hot-tip"><fire-icon /> Hot Tip: You can add up to two effects</div>
    <div
      v-for="(effectGroup, index) in effectGroups" :key="index"
      class="effects-gallery__item"
      :class="{ 'effects-gallery__item--selected': isEffectGroupSelected(effectGroup) }"
      @click.self="clickEffectGroup(effectGroup)"
    >
      <lock-icon class="effects-gallery__item-icon effects-gallery__item-icon--locked" 
        @click="projectStore.toggleLock(effectGroup.name)" v-if="effectGroupHasLockedEffect(effectGroup)" />
      <solid-check-circle class="effects-gallery__item-icon" v-else/>
      <union-with-circle-icon class="effects-gallery__item-icon effects-gallery__item-icon--hover"/>
      <div class="effects-gallery__item-shadow" />
      <div class="effects-gallery__item-name">
        {{ effectGroup.name }}
      </div>
      <picture-helper 
        class="effects-gallery__item-image"
        :image-name="effectGroup.effects[0].thumbnail"
        :densities="['1x','2x']"
        :formats="['webp', 'jpg']"
      />
    </div>
    <transition name="effects-gallery__expand">
      <effect-controls-panel
        v-if="selectedEffectGroup && selectedEffect"
        :effect-group="selectedEffectGroup"
        :selected-available-effect="selectedEffect"
        @select-effect="(effect) => onSelectEffect(effect)"
      />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { effectGroups, EffectGroup, AvailableEffect } from '@/static/effectName'
import SolidCheckCircle from '@/assets/SolidCheckCircle.svg'
import LockIcon from '@/assets/LockWithCircleIcon.svg'
import FireIcon from '@/assets/FireIcon.svg'
import UnionWithCircleIcon from '@/assets/UnionWithCircle.svg'
import PictureHelper from '@/components/effect/PictureHelper.vue'
import EffectControlsPanel from '@/components/effect/EffectControlsPanel.vue'
import { useProjectStore } from '@/store/project-store'
import useEffect from '@/composables/UseEffect'
import { ref } from 'vue'

const projectStore = useProjectStore()

const selectedEffectGroup = ref<EffectGroup | null>(null)
const selectedEffect = ref<AvailableEffect | null>()
const { addEffect, removeEffect } = useEffect()

const onSelectEffect = (effect: AvailableEffect) => {
  if (effect !== selectedEffect.value) {
    selectedEffect.value = effect
    addEffect(effect)
  }
}

const maxEffects = 2

const isEffectGroupSelected = (effectGroup: EffectGroup) => effectGroup.effects.some(effect => projectStore.activeEffectStates.has(effect.groupName))

const clickEffectGroup = (effectGroup: EffectGroup) => {
  if (selectedEffectGroup.value?.name === effectGroup.name) {
    if (!projectStore.activeEffectStates.get(effectGroup.name)?.isLocked) {
      deselectEffectGroup(effectGroup)
    }
  } else {
    if (projectStore.activeEffectStates.get(effectGroup.name)) {
      selectEffectGroup(effectGroup)
    } else if (projectStore.countLockedEffect < maxEffects) {
      if (projectStore.activeEffectStates.size === maxEffects) {
        removeFirstUnlockedEffectGroup()
      }
      selectEffectGroup(effectGroup)
    }
  }
}

const removeFirstUnlockedEffectGroup = () => {
  const firstUnlockedEffect = [...projectStore.activeEffectStates.values()].find(effectState => !effectState.isLocked)
  if (firstUnlockedEffect) {
    removeEffect(firstUnlockedEffect.groupName)
  }
}

const selectEffectGroup = (effectGroup: EffectGroup) => {
  selectedEffectGroup.value = effectGroup
  onSelectEffect(effectGroup.effects.find(effect => projectStore.activeEffectStates.get(effect.groupName)?.name === effect.name) ?? effectGroup.effects[0])
}

const deselectEffectGroup = (effectGroup: EffectGroup) => {
  selectedEffectGroup.value = null
  selectedEffect.value = null
  const appliedEffectFromGroup = effectGroup.effects.find(effect => projectStore.activeEffectStates.has(effect.groupName))
  if (appliedEffectFromGroup) {
    removeEffect(appliedEffectFromGroup.groupName)
  }
}

const effectGroupHasLockedEffect = (effectGroup: EffectGroup) => {
  return effectGroup.effects.some(effect => projectStore.activeEffectStates.get(effect.groupName)?.isLocked)
}

</script>

<style lang="scss">
.effects-gallery {
  $root: #{&};
  padding-left: 30px;
  padding-top: 10px;
  overflow-y: scroll;
  padding-bottom: 307px;

  &::-webkit-scrollbar {
    display: none;
  }

  &__item {
    position: relative;
    cursor: pointer;
    $item-width: 170px;
    $item-height: 170px;
    $item-border-radius: 4px;
    width: $item-width;
    height: $item-height;
    float: left;
    margin: 3px;
    border-radius: $item-border-radius;

    &-image img {
      width: $item-width;
      height: $item-height;
      border-radius: $item-border-radius;
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
        border: 1px solid transparent;
        transition: border 150ms $animationCurve;
    }

    &:hover {
      &::after {
        border: 1px solid $white;
      }

      // mousedown
      &:active::after {
        border: 3px solid $white;
      }
    }

    &--selected {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
        border: 2px solid $white;
      }

      // selected show check icon
      #{$root}__item-icon {
        opacity: 100%;

        &--hover {
          opacity: 0%;
        }
      }

      &:hover {
        &::after {
          border: 2px solid $white;
        }

        // selected hover
        #{$root} {
          &__item-icon {
            opacity: 0%;
          }

          &__item-icon--hover {
            opacity: 100%;
          }
          &__item-icon--locked {
            opacity: 100%;
          }
        }
      }
    }

    &-icon {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 22px;
      height: 22px;
      opacity: 0%;
      transition: all 200ms $animationCurve;

      > circle {
        // change solid check circle to be grey
        fill: $grey-80;
      }
      &--locked {
        > circle {
          // change solid check circle to be purple
          fill: $purple;
        }
      }
    }

    &-name {
      position: absolute;
      bottom: 21px;
      color: $white;
      left: 0;
      right: 0;
      text-align: center;
      font-family: $font-regular;
      font-size: 14px;
      line-height: 17px;
    }

    &-shadow {
      position: absolute;
      height: 49px;
      width: 100%;
      bottom: 0;
      left: 0;
      border-radius: 0 0 4px 4px;
      background-image: linear-gradient(to bottom, rgba($black, 0), rgba($black, 0.5));
    }
  }
  &__hot-tip {
    color: white;
    font-size: 14px;
    line-height: 17px;
    padding: 15px 30px 16px 0;
    text-align: center;
    font-family: $font-regular;
  }
  &__expand {
    &-enter-active,
    &-leave-active {
      transition: max-height 400ms $animationCurve;
    }

    &-enter-from,
    &-leave-to {
      max-height: 0;
    }
  }
}
</style>
